import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { InformationIcon, LocationOneIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useTranslation } from 'react-i18next'

import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { TripStepTypeEnum } from '../../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TransportMeansLight } from '../../../../lib/types/models/transport-means/transport-means'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { TripBasic } from '../../../../lib/types/models/trip/trip'
import { TripAddressCompact } from '../../../../lib/types/models/trip-address/trip-address'
import { TripBookingProperties } from '../../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripBookingTypes } from '../../../../lib/types/models/trip-booking/trip-booking'
import {
  TripBookingDispatchPlaceLight,
  TripBookingDispatchPlacePartial
} from '../../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place'
import {
  Appointment,
  TripBookingDispatchPlaceProperties
} from '../../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place.properties'
import { Time } from '../../../../lib/types/utils'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { getTravelScheduleTripTypeOption } from '../../../../lib/utils/functions/travel-schedule.functions'
import TransportPicto from '../../../elements/transport-picto'
import { LabelValue } from '../../language-test/client-space/shared-client-space-ui'
import { MeetingPlaceSection } from '../../language-test/client-space/shared-client-space-ui/meeting-place-section'
import ReservationTime from '../../trip-folders/trip-folder-reservation/reservation-time'
import { convertTimeZone } from '../../trip-folders/trip-folder-reservation/trip-folder-reservation-details'

function convertTimeZoneToLocal(date: Date | string, time: Time, timezone: string) {
  return DateFormatter.convertTimeZone(time, timezone, 'Local', new Date(date))
}

type TravelScheduleDispatchPlaceCardProps = {
  dispatchPlace: TripBookingDispatchPlacePartial
  hideTripStepDetails?: boolean
  hasReturnTrip?: boolean
}

function getTravelScheduleDispatchPlaceCardLabels(directionality: TripStepTypeEnum) {
  const { t } = useTranslation()

  if (directionality === TripStepTypeEnum.INBOUND)
    return {
      dateLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.date.of.departure'),
      addressLabel: t(
        'staff.personal.space.travel.schedule.dispatch.place.card.departure.address'
      ),
      hoursLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.rdv.meeting.time'),
      placeLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.departure.place'),
      timeLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.departure.time')
    }

  return {
    dateLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.date.of.arrival'),
    addressLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.arrival.address'),
    hoursLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.arrival.time'),
    placeLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.arrival.place'),
    timeLabel: t('staff.personal.space.travel.schedule.dispatch.place.card.arrival.time')
  }
}

function getAppointment<T extends TripBookingDispatchPlaceLight | TripBookingDispatchPlacePartial>(
  dp: T
): Appointment<T> {
  if (dp.tripBooking?.isTakeInCharge)
    return {
      time: dp.tripBooking?.meetingTimeForStaff,
      place: dp.tripBooking?.meetingPlace
    } as Appointment<T>

  if (dp.directionality === TripStepTypeEnum.INBOUND) {
    const time = dp.tripStep?.timeBeforeDepartureForStaff
    const place = dp.tripStep?.departurePlace
    return { time, place } as Appointment<T>
  }

  // OUTBOUND
  return {
    time: dp.tripStep?.departureTime,
    place: dp.tripStep?.arrivalPlace
  } as Appointment<T>
}

export function TravelScheduleDispatchPlaceCard({
  dispatchPlace,
  hideTripStepDetails = false,
  hasReturnTrip = false
}: TravelScheduleDispatchPlaceCardProps) {
  const { t } = useTranslation()
  const { directionality } = dispatchPlace
  const { tripBooking, tripStep } = dispatchPlace
  const { modalState, selectedData, toggleModal } = useModalManager<
    'address-details',
    TripAddressCompact
  >(['address-details'])

  // don't use the time from the getTripInfo function because it's the time before the departure or arrival
  const { date, timezone, company } = TripBookingDispatchPlaceProperties.getTripInfo(dispatchPlace)

  const appointment = getAppointment(dispatchPlace)
  const time =
    directionality === TripStepTypeEnum.INBOUND ? appointment.time : tripStep?.arrivalTime

  const { label, variant } = getTravelScheduleTripTypeOption(directionality, hasReturnTrip)

  const { dateLabel, addressLabel, hoursLabel, placeLabel, timeLabel } =
    getTravelScheduleDispatchPlaceCardLabels(directionality)

  const tripAddress = appointment.place

  if (!tripBooking) return 'No trip booking found'
  return (
    <Box style="border" className={'bg-white-hover h-fit'}>
      <Badge text={`Voyage ${label.toLowerCase()}`} variant={variant} />

      <Box className="grid mobile:grid-cols-1 tab:grid-cols-1 grid-cols-3 gap-2 bg-primary-light rounded p-3">
        <LabelValue label={dateLabel} color={tailwindColors.grey.lighter}>
          <Badge
            variant={'secondaryRed'}
            text={valueRender(date, (d) => DateFormatter.DDDMYYYY(d))}
          />
        </LabelValue>

        <LabelValue label={addressLabel} color={tailwindColors.grey.lighter}>
          {tripAddress ? (
            <div onClick={() => toggleModal('address-details', tripAddress)}>
              <InformationIcon
                color={tailwindColors.vividBlue.DEFAULT}
                className="min-w-4 size-4 inline"
              />
              <Typography.Link className="inline ml-1" variant="tag">
                {`${tripAddress.label} - ${tripAddress.address1}`}
              </Typography.Link>
            </div>
          ) : (
            '-'
          )}
        </LabelValue>

        <LabelValue label={hoursLabel} color={tailwindColors.grey.lighter}>
          <Badge
            variant={'secondaryRed'}
            text={valueRender(time, (time) =>
              convertTimeZone(time, 'Local', timezone).replace(':', 'h')
            )}
          />
        </LabelValue>

        <LabelValue
          label={t('staff.personal.space.travel.schedule.dispatch.place.card.company')}
          color={tailwindColors.grey.lighter}
        >
          <Typography variant="tag">{valueRender(company)}</Typography>
        </LabelValue>
      </Box>

      {tripStep && !hideTripStepDetails && (
        <div className={'grid mobile:grid-cols-1 tab:grid-cols-1 grid-cols-3 gap-2 px-3'}>
          <LabelValue label={dateLabel} color={tailwindColors.grey.lighter}>
            <Badge text={valueRender(date, (d) => DateFormatter.DDMMYYYY(d))} />
          </LabelValue>

          <LabelValue label={placeLabel} color={tailwindColors.grey.lighter}>
            <Typography variant={'tag'}>{valueRender(tripStep.departurePlace.label)}</Typography>
          </LabelValue>

          <LabelValue label={timeLabel} color={tailwindColors.grey.lighter}>
            <Typography variant={'tag'}>
              {valueRender(tripStep.departureTime, (t) =>
                DateFormatter.HHmm(DateFormatter.createDate(t))
              )}
            </Typography>
          </LabelValue>
        </div>
      )}

      {selectedData && (
        <Modal
          icon={LocationOneIcon}
          title={selectedData.address1}
          isOpen={modalState['address-details']}
          onCancel={() => toggleModal('address-details')}
          size="large"
          hideActions
        >
          <div className={MODAL_CONTENT_CLASSNAME}>
            <MeetingPlaceSection id={selectedData.id} />
          </div>
        </Modal>
      )}
    </Box>
  )
}

type TravelScheduleDispatchPlaceDetailedCardProps = {
  dispatchPlace: TripBookingDispatchPlacePartial
  directionality: TripStepTypeEnum
  hasReturnTrip?: boolean
}

type ReservationDetailsProps = {
  tripBooking: TripBookingTypes.Slim & {
    relatedTrip: TripBasic
    reservationNumber: string
    transportMeans: TransportMeansLight
  }
}

function ReservationDetails({ tripBooking }: ReservationDetailsProps) {
  const { t } = useTranslate()
  const trip = tripBooking?.relatedTrip
  const tripSteps = TripBookingProperties.sortTripStepsById(tripBooking?.tripSteps) ?? []
  const device = useBreakPoint()
  const { modalState, selectedData, toggleModal } = useModalManager<
    'address-details',
    TripAddressCompact
  >(['address-details'])

  return (
    <div className="space-y-2">
      <div className="flex w-full gap-2 items-center">
        {trip?.transportMeans?.picto && <TransportPicto picto={trip.transportMeans?.picto} />}
        <Typography variant="secondary-body" color={tailwindColors.secondary.DEFAULT}>
          {TripProperties.getTripLabel(trip)}
        </Typography>
      </div>

      <Box style="border" className="bg-white-dark">
        {tripSteps && tripSteps.length > 0 && (
          <div className="w-full bg-white-dark rounded p-1.5 flex flex-col gap-2">
            <Badge
              variant={'white'}
              color={tailwindColors.white.active}
              className={'h-fit py-1 w-fit'}
              text={
                <div className="flex items-center gap-2 mobile:items-start tab:items-start mobile:flex-col tab:flex-col">
                  {tripBooking?.reservationNumber && (
                    <Typography variant={'label-extrabold'}>
                      {tripBooking.reservationNumber}
                    </Typography>
                  )}
                  {tripBooking?.travelCompany?.name && (
                    <Typography variant={'label-extrabold'}>
                      {`(${tripBooking.travelCompany.name})`}
                    </Typography>
                  )}
                  <div className={'flex items-center gap-1'}>
                    <Typography variant={'label-semibold'}>Départ</Typography>
                    <Badge
                      text={valueRender(tripSteps.at(0)?.departureTime, (time) =>
                        convertTimeZoneToLocal(
                          tripSteps.at(0)?.departureDate ?? new Date(),
                          time,
                          tripSteps.at(0)?.departureTimeZone ?? 'Local'
                        )
                      )}
                      variant="primary"
                      size="xsmall"
                    />
                  </div>
                  <span className="mobile:hidden tab:hidden">-</span>
                  <div className="flex items-center gap-1">
                    <Typography variant={'label-semibold'}>Arrivée</Typography>
                    <Badge
                      text={valueRender(tripSteps.at(-1)?.arrivalTime, (time) =>
                        convertTimeZoneToLocal(
                          tripSteps.at(-1)?.arrivalDate ?? new Date(),
                          time,
                          tripSteps.at(-1)?.arrivalTimeZone ?? 'Local'
                        )
                      )}
                      variant={'primary'}
                      size={'xsmall'}
                    />
                  </div>
                </div>
              }
            />

            {tripSteps.map((step) => (
              <div
                key={step.id}
                className={cn('grid grid-cols-2 w-full pl-2 gap-2', {
                  'grid-cols-1': device === 'mobile'
                })}
              >
                {/* First row */}
                <div className={'mr-1 w-full flex items-center flex-row gap-1'}>
                  <Typography.Link
                    variant={'label-semibold'}
                    onClick={() => {
                      if (step.departurePlace) toggleModal('address-details', step.departurePlace)
                    }}
                  >
                    {valueRender(step.departurePlace?.label, (label) => label ?? '')}
                  </Typography.Link>
                  <span>-</span>
                  <Typography.Link
                    variant={'label-semibold'}
                    onClick={() => {
                      if (step.arrivalPlace) toggleModal('address-details', step.arrivalPlace)
                    }}
                  >
                    {valueRender(step.arrivalPlace?.label, (label) => label ?? '')}
                  </Typography.Link>
                  <span className="text-blue">{' :'}</span>
                </div>
                <div className="flex items-baseline gap-1 mobile:flex-col tab:flex-col">
                  <ReservationTime
                    label={t(
                      'staff.personal.space.travel.schedule.group.itinerary.reservation.departure.label'
                    )}
                    time={valueRender(step.departureTime, (time) =>
                      convertTimeZoneToLocal(
                        step.departureDate ?? new Date(),
                        time,
                        step.departureTimeZone ?? 'Local'
                      )
                    )}
                  />
                  <span className="mobile:hidden tab:hidden">-</span>
                  <ReservationTime
                    label={t(
                      'staff.personal.space.travel.schedule.group.itinerary.reservation.arrival.label'
                    )}
                    time={valueRender(step.arrivalTime, (time) =>
                      convertTimeZoneToLocal(
                        step.arrivalDate ?? new Date(),
                        time,
                        step.arrivalTimeZone ?? 'Local'
                      )
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Box>

      {selectedData && (
        <Modal
          icon={LocationOneIcon}
          title={selectedData.label}
          isOpen={modalState['address-details']}
          onCancel={() => toggleModal('address-details')}
          size="large"
          hideActions
        >
          <div className={MODAL_CONTENT_CLASSNAME}>
            <MeetingPlaceSection id={selectedData.id} />
          </div>
        </Modal>
      )}
    </div>
  )
}

export function TravelScheduleDispatchPlaceDetailedCard({
  dispatchPlace,
  directionality,
  hasReturnTrip = false
}: TravelScheduleDispatchPlaceDetailedCardProps) {
  const { t } = useTranslation()
  const { tripBooking } = dispatchPlace
  const { label, variant } = getTravelScheduleTripTypeOption(
    dispatchPlace.directionality,
    hasReturnTrip
  )
  const { modalState, selectedData, toggleModal } = useModalManager<
    'address-details',
    TripAddressCompact
  >(['address-details'])

  const tripStep = TripBookingProperties.getTripStep(tripBooking, directionality)
  const tripAddress = tripStep?.foreignProviderAppointmentPlace

  return (
    <Box style="border" className="bg-white-hover">
      <Badge text={`Voyage ${label.toLowerCase()}`} variant={variant} />

      <Box
        style="border"
        className={cn('bg-white', {
          'flex-col-reverse': directionality === TripStepTypeEnum.OUTBOUND
        })}
      >
        {/* reservation details */}
        <div className="space-y-2">
          <Typography variant="text">
            {t('staff.personal.space.travel.schedule.group.itinerary.reservation.title')}
          </Typography>

          <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1 tab:grid-cols-1">
            {tripBooking && <ReservationDetails tripBooking={tripBooking} />}

            {tripBooking?.secondaryReservation && (
              <ReservationDetails tripBooking={tripBooking.secondaryReservation} />
            )}
          </div>
        </div>

        {/* RDV hours */}
        <div className="space-y-2">
          <Typography variant="text">
            {t(
              directionality === TripStepTypeEnum.OUTBOUND
                ? 'staff.personal.space.travel.schedule.group.itinerary.reservation.return.transfer.title'
                : 'staff.personal.space.travel.schedule.group.itinerary.reservation.departure.transfer.title'
            )}
          </Typography>

          <div className="grid grid-cols-2 gap-2 mobile:grid-cols-1 tab:grid-cols-1">
            <LabelValue
              color={tailwindColors.grey.lighter}
              label={t(
                'staff.personal.space.travel.schedule.group.itinerary.foreign.provider.place.lable',
                {
                  name:
                    directionality === TripStepTypeEnum.INBOUND
                      ? undefined
                      : getTranslatedName(tripBooking?.transportMeans)
                }
              )}
            >
              {tripAddress ? (
                <Typography.Link
                  variant="tag"
                  onClick={() => toggleModal('address-details', tripAddress)}
                >
                  {`${tripAddress.label} - ${tripAddress.address1}`}
                </Typography.Link>
              ) : (
                '-'
              )}
            </LabelValue>

            <LabelValue
              color={tailwindColors.grey.lighter}
              label={t(
                'staff.personal.space.travel.schedule.group.itinerary.foreign.provider.appointment.label',
                {
                  name:
                    directionality === TripStepTypeEnum.INBOUND
                      ? undefined
                      : getTranslatedName(tripBooking?.transportMeans)
                }
              )}
            >
              <Badge
                text={valueRender(
                  directionality === TripStepTypeEnum.INBOUND
                    ? tripStep?.foreignProviderAppointmentTime
                    : tripStep?.timeBeforeDepartureForGroup,
                  (value) =>
                    convertTimeZoneToLocal(
                      directionality === TripStepTypeEnum.INBOUND
                        ? (tripStep?.departureDate ?? new Date())
                        : (tripStep?.arrivalDate ?? new Date()),
                      value,
                      tripStep?.[
                        directionality === TripStepTypeEnum.INBOUND
                          ? 'arrivalTimeZone'
                          : 'departureTimeZone'
                      ] ?? 'Local'
                    ).replace(':', 'h')
                )}
              />
            </LabelValue>
          </div>
        </div>
      </Box>

      {selectedData && (
        <Modal
          icon={LocationOneIcon}
          title={selectedData.address1}
          isOpen={modalState['address-details']}
          onCancel={() => toggleModal('address-details')}
          size="large"
          hideActions
        >
          <div className={MODAL_CONTENT_CLASSNAME}>
            <MeetingPlaceSection id={selectedData.id} />
          </div>
        </Modal>
      )}
    </Box>
  )
}
